<script setup>
import {
  ref,
  useAsyncData,
  useEventTracking,
  useHead,
  useRoute,
  useSanity,
  useSanityQuery,
  useScrollTopOnMounted,
  watch,
} from '#imports';
import { getQuery as getArticlesQuery } from '~/groq-queries/articles';
import { getQuery as getTotalCount } from '~/groq-queries/articles-count';
import { getQuery as getSearch } from '~/groq-queries/search';
import { getCommonMetaTags } from '~/utils/get-common-meta-tags';
import { uniqBy } from 'lodash-es/array';

useScrollTopOnMounted();
const route = useRoute();

const title = 'Все материалы DOXA';
const description =
  'Здесь можно посмотреть все материалы, гайды и видео, которые выпускала DOXA';
const ARTICLES_URL = 'https://ei3mgr4e-bsccljbcrq-ez.a.run.app/articles';

useHead({
  title,
  meta: getCommonMetaTags({
    path: route.fullPath,
    ogType: 'website',
    title,
    description,
  }),
  script: [
    {
      key: 'ld-json-articles',
      type: 'application/ld+json',
      children: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: title,
        description,
        url: ARTICLES_URL,
        mainEntity: {
          '@type': 'ItemList',
          name: 'Статьи DOXA',
          description: 'Все материалы, статьи и публикации медиа DOXA',
          url: ARTICLES_URL,
        },
        isPartOf: {
          '@type': 'WebSite',
          name: 'DOXA',
          url: 'https://ei3mgr4e-bsccljbcrq-ez.a.run.app',
        },
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': ARTICLES_URL,
        },
      }),
    },
  ],
});

const sanity = useSanity();
const articlesChunkSize = 10;
const articlesBlockSize = 0;
const offset = ref(articlesBlockSize);
const limit = ref(articlesChunkSize);
const showMoreButton = ref(true);
const errorMessage = ref();
const searchPerformed = ref(false);

const searchQuery = ref('');
const searchResults = ref([]);
const searchEmpty = ref(false);

const { data: articles } = await useSanityQuery(
  getArticlesQuery({ limit: articlesChunkSize })
);

const { data: totalCount } = await useSanityQuery(getTotalCount());

if (
  articles.value.length < articlesChunkSize ||
  articles.value.length === totalCount.value
)
  showMoreButton.value = false;

const {
  pending,
  data: moreArticlesChunk,
  refresh,
} = await useAsyncData('moreArticles', () =>
  sanity.fetch(
    getArticlesQuery({
      offset: offset.value,
      limit: limit.value,
    })
  )
);

async function fetchMoreArticles() {
  offset.value += articlesChunkSize;
  limit.value += articlesChunkSize;
  useEventTracking('click', 'Show more articles');

  await refresh();
  articles.value = [...articles.value, ...moreArticlesChunk.value];

  if (
    moreArticlesChunk.value.length < articlesChunkSize ||
    articles.value.length === totalCount.value
  ) {
    showMoreButton.value = false;
  }
}

async function performSearch(event) {
  event.preventDefault();
  searchPerformed.value = true;

  if (searchQuery.value.trim() === '') {
    return;
  }

  try {
    useEventTracking('click', 'User used search');
    const { query } = getSearch({
      searchQuery: searchQuery.value,
      offset: offset.value,
      limit: limit.value,
    });
    const results = await sanity.fetch(query);

    if (results.length === 0) {
      searchEmpty.value = true;
    } else {
      searchEmpty.value = false;
      searchResults.value = uniqBy(results, (result) => result._id);
    }
  } catch (error) {
    errorMessage.value = error.message;
  }
}

watch(searchQuery, (newValue) => {
  if (newValue === '') {
    searchEmpty.value = false;
    searchResults.value = [];
    searchPerformed.value = false;
  }
});
</script>

<template>
  <LayoutDefaultPage is-header-shown>
    <div class="articles content-container">
      <div class="articles__content">
        <h1 class="articles__title">Все материалы DOXA</h1>
        <form @submit.prevent="performSearch">
          <div class="articles__search">
            <BaseInputText
              v-model="searchQuery"
              class="articles__search-input"
              placeholder="Поиск материалов"
              type="search"
              @onclick.prevent="performSearch"
            />
            <ButtonComponent
              button-caption="Найти"
              theme="purple"
              @onclick.prevent="performSearch"
            />
          </div>
        </form>

        <Transition name="fade" mode="out-in" type="transition">
          <SearchResults
            v-show="searchResults"
            :error-message="errorMessage"
            :search-empty="searchEmpty"
            :search-results="searchResults"
          />
        </Transition>

        <Transition name="fade" mode="out-in" type="transition">
          <div v-show="searchEmpty">
            <div class="articles__search-empty">
              <h2>
                По этому запросу ничего не найдено, но ниже будут наши последние
                материалы — они тоже супер!
              </h2>
            </div>

            <ul class="articles__list">
              <li v-for="articleItem in articles" :key="articleItem._id">
                <a
                  v-if="articleItem.isArticleExternalSourceType"
                  :href="articleItem.externalSource"
                  target="_blank"
                >
                  <ArticleItem :article="articleItem" />
                </a>
                <NuxtLink
                  v-else
                  :to="{
                    name: 'articles-slug',
                    params: { slug: articleItem.slug.current },
                  }"
                >
                  <ArticleItem :article="articleItem" />
                </NuxtLink>
              </li>
            </ul>
            <div v-show="showMoreButton" class="articles__more-button">
              <MoreContentButton
                :loading="pending"
                @click="fetchMoreArticles"
              />
            </div>
          </div>
        </Transition>

        <div
          v-if="
            searchEmpty === true ||
            searchResults.length === 0 ||
            (searchResults.length > 0 && searchEmpty === true) ||
            !(searchResults.length > 0 && searchEmpty === false)
          "
        >
          <ul class="articles__list">
            <li v-for="articleItem in articles" :key="articleItem._id">
              <a
                v-if="articleItem.isArticleExternalSourceType"
                :href="articleItem.externalSource"
                target="_blank"
              >
                <ArticleItem :article="articleItem" />
              </a>
              <NuxtLink
                v-else
                :to="{
                  name: 'articles-slug',
                  params: { slug: articleItem.slug.current },
                }"
              >
                <ArticleItem :article="articleItem" />
              </NuxtLink>
            </li>
          </ul>
          <div v-show="showMoreButton" class="articles__more-button">
            <MoreContentButton :loading="pending" @click="fetchMoreArticles" />
          </div>
        </div>
      </div>
    </div>
  </LayoutDefaultPage>
</template>

<style lang="postcss" scoped>
.articles {
  & .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.1s;
  }

  & .fade-enter,
  .fade-leave-to {
    opacity: 0;
  }

  &__search {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 16px;
    padding-bottom: 16px;

    &-input {
      width: 100%;
    }

    &-empty {
      text-align: center;
      margin-top: 16px;
      margin-bottom: 48px;

      @media (max-width: 425px) {
        h2 {
          font-size: 16px;
          line-height: 22px;
        }
      }
    }
  }

  &__content {
    max-width: 825px;
    margin: 0 auto;
    padding: 60px 0 160px 0;
  }

  &__title {
    font-size: var(--h1-font-size);
    line-height: var(--h1-line-height);
    font-weight: 700;
    margin-bottom: 24px;
  }

  &__subtitle {
    font-size: var(--body-font-size);
    line-height: var(--body-line-height);
    font-weight: 500;
    margin-bottom: 40px;
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  &__more-button {
    display: flex;
    justify-content: center;
    margin-top: 32px;
  }

  @media (--small-vp) {
    &__content {
      padding: 32px 0 60px 0;
    }

    &__title {
      font-size: 24px;
      line-height: 24px;
      margin-bottom: 16px;
    }

    &__subtitle {
      font-size: 16px;
      line-height: 22px;
      margin-bottom: 32px;
    }

    &__list {
      gap: 8px;
    }

    &__search {
      flex-direction: column;
    }
  }
}
</style>
